/**
 * Whether a rail row is the one the person is currently inside.
 *
 * <h2>A child route keeps its parent lit</h2>
 *
 * `/email-warmup/create-account` is inside Email Warmup, so the Email Warmup row is
 * current. Exact equality says otherwise, which is how the row went dark the moment anybody
 * opened a mailbox or the create form — the screen was reached from that row and the rail
 * then claimed they were nowhere.
 *
 * This is the design's own rule, written at `AppNav.tsx:611` as the fallback under its pile
 * of special cases:
 *
 * ```js
 * return location.pathname === href || location.pathname.startsWith(href + '/');
 * ```
 *
 * Its Email Warmup case (`:601-605`) is that same rule plus two `/app/`-prefixed aliases
 * this application does not have, so the general form reproduces it exactly.
 *
 * <h2>The trailing slash is the whole safety of it</h2>
 *
 * `startsWith(route)` alone would light Email Warmup for `/email-warmup-settings`, a
 * different screen whose path merely begins with the same letters. Comparing against
 * `route + '/'` matches descendants and nothing else, which is why the design writes it
 * that way rather than as a bare prefix test.
 *
 * <h2>`aria-current` is deliberately NOT this</h2>
 *
 * The rows are `NavLink`s carrying `end`, so react-router marks `aria-current` on the exact
 * match alone — and that stays. A screen reader is told about **one** current page, which
 * is true: `/email-warmup/create-account` is not the Email Warmup page, it is a page inside
 * it. The eye is shown the group instead, because a rail with nothing lit reads as being
 * nowhere.
 *
 * <p>So the two answers differ on purpose, and this function is only ever the paint.
 */
export function isRouteActive(route: string, pathname: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}
